import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { UserDto } from '../../dtos/user'
import { simpleNotification, validateSession } from '../../_utils/utils'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../stores/app'
import { sendGAEvent } from '../../components/ga-helper/ga-helper'
import { AppService, UserService } from '../../services/index'
import { AUTH0_PLATFORM_REDIRECT_PATHNAME, AUTH0_STATE_SESSION_KEY } from '../../constants'
import { ModuleLoadingScreen } from '../../components/module-loading-screen/module-loading-screen'
import { PlatformIdentityContext } from '../platform-identity-provider/platform-identity-provider'
import { buildAuthorizeRoute } from '../../_utils/routing'
import axios from 'axios'
import aqe from '@pushly/aqe'
import { useHistory, useLocation } from 'react-router-dom'
import { parseQueryStringParams } from '../../_utils/query-string'
import { Auth0InvalidAuthorizationParamsException } from '../../exceptions/auth0-invalid-auth-params.exception'
import { SessionValidationFailedException } from '../../exceptions/session-validation-failed.exception'
import { ErrorPage } from '../../components/error-page/error-page'
import { PushlyAuth0State } from '../../components/auth0-redirect/auth-redirect'
import { platformUserReducer } from '@pushly/aqe/lib/providers/user-preferences-provider/internal/reducer'
import { broadcastSessionRestored } from '@pushly/aqe/lib/utils/session/session-state-sync'
import {
    PlatformUserContext as BasePlatformUserContext,
    PlatformUserDispatchContext,
    UserContextAction,
    UserPreferencesContext,
} from '@pushly/aqe/lib/providers/user-preferences-provider/internal'
import { PreferenceService } from '@pushly/aqe/lib/services/preference.service'
import { Container } from 'typescript-ioc'
import { PlatformUserDomainRecord } from '../../models/domain/platform-user-domain-record'

export enum UserReviewStatus {
    PENDING = 'pending',
    COMPLETED = 'completed',
    DISMISSED = 'dismissed',
    ACKNOWLEDGED = 'acknowledged',
}

export type PlatformUser =
    | {
          authenticated: false
          revalidate?: boolean
          preferences: UserPreferencesContext
          loading: boolean
      }
    | {
          authenticated: true
          user: UserDto
          revalidate?: boolean
          preferences: UserPreferencesContext
          loading: boolean
          reviewState: {
              status: UserReviewStatus
              url?: string
              loading?: boolean
          }
      }

type UserContext = PlatformUser & { assignUser?: React.Dispatch<React.SetStateAction<PlatformUser>> }

const initialPlatformUser: PlatformUser = {
    authenticated: false,
    preferences: {
        state: {
            bundle: null,
            loading: true,
        },
        refresh: async () => {},
    },
    loading: false,
}
export const PlatformUserContext = React.createContext<UserContext>(initialPlatformUser)

export const UserProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const loc = useLocation()
    const idpContext = useContext(PlatformIdentityContext)

    const appState = useService(AppState)
    const appService = useService(AppService)
    const preferenceService = Container.get(PreferenceService)
    const userSvc = useService(UserService)

    const history = useHistory()
    appService.attachHistoryContainer(history)

    const [userContext, dispatch] = useReducer(platformUserReducer, initialPlatformUser)

    const [reviewState, setReviewState] = useState<{
        status: UserReviewStatus
        url?: string
        loading?: boolean
    }>({
        status: UserReviewStatus.PENDING,
        loading: false,
    })

    const combinedContextValue = useMemo<PlatformUser>(
        () =>
            userContext.authenticated
                ? {
                      ...userContext,
                      reviewState,
                  }
                : {
                      ...userContext,
                  },
        [userContext, reviewState],
    )

    const [initialUserContextLoaded, setInitialUserContextLoaded] = useState<boolean>(false)
    const [loadError, setLoadError] = useState<Error | null>(null)
    const [appStateInitialized, setAppStateInitialized] = useState<boolean>(false)
    const inAuth0Flow = useRef(false)

    const assignUser = useCallback(
        (update: React.SetStateAction<PlatformUser>) => {
            if (typeof update === 'function') {
                setReviewState((prev) => {
                    // Note: We only support updating reviewState via assignUser for now
                    // as the rest of the userContext is managed by the reducer.
                    const next = update({ ...combinedContextValue } as PlatformUser)
                    if (next.authenticated) {
                        return next.reviewState
                    }
                    return prev
                })
            } else {
                if (update.authenticated) {
                    setReviewState(update.reviewState)
                }
            }
        },
        [combinedContextValue],
    )

    const handleLoadError = useCallback((error: Error) => {
        setLoadError(error)

        // Don't set isAuthenticated to false if this is a session expiration
        // We want to keep the user as "authenticated but expired" so the session
        // expiration screen shows instead of redirecting to login
        if (!(error instanceof SessionValidationFailedException) || !appState.sessionExpired) {
            appState.isAuthenticated = false
        }

        appService.unsetAppLoading()

        setInitialUserContextLoaded(true)
    }, [])

    const authorizeAndLoadUserViaAuth0Code = useCallback(async (code: string) => {
        if (!idpContext.loaded) {
            // FIXME: extract error handling to HOC at highest level and capture
            throw new Error('IDP not available')
        }

        window.sessionStorage.removeItem(AUTH0_STATE_SESSION_KEY)

        const label = idpContext.whiteLabelSettings
        const query = [
            `code=${code}`,
            `redirect_uri=${buildAuthorizeRoute(idpContext.whiteLabelSettings.getHostname())}`,
            `tenant_id=${label.getAuth0TenantId()}`,
            `client_id=${label.getAuth0PlatformClientId()}`,
        ].join('&')

        try {
            const res = await axios.get<any>(`${aqe.defaults.publicApiDomain}/auth/token?${query}`, {
                bypassGlobalErrorHandlers: true,
            } as any)

            if (res.status === 200) {
                await setupInitialUserContext(UserDto.fromApiResponse(res.data.data))

                const redirect = localStorage.getItem('refreshRedirectURI')
                localStorage.removeItem('refreshRedirectURI')

                if (redirect) {
                    appService.route(redirect)
                } else {
                    appService.routeWithinDomain('/dashboard')
                }
            }
        } catch (error) {
            handleLoadError(error)
        } finally {
            inAuth0Flow.current = false
        }
    }, [])

    const loadUserViaSession = async () => {
        let user: UserDto
        try {
            const validatedUser = await validateSession()
            user = UserDto.fromApiResponse(validatedUser)

            await setupInitialUserContext(user!)
        } catch (error) {
            const wrappedError = new SessionValidationFailedException(error)
            handleLoadError(wrappedError)
        }
    }

    const loadUserBootstrapPreferences = useCallback(
        async (userId?: number, namespace?: string) => {
            try {
                if (!userId && !appState.currentUser?.id) {
                    return
                }

                const targetUserId = userId || appState.currentUser?.id

                dispatch({ type: UserContextAction.BOOTSTRAP_START })

                const response = await preferenceService.fetchBootstrapPreferences(targetUserId!, 'pufferfish')

                if (response.data) {
                    const bundle = response.data

                    const preferencesContext: UserPreferencesContext = {
                        state: {
                            bundle,
                            loading: false,
                        },
                        refresh: async () => {
                            return loadUserBootstrapPreferences(targetUserId, namespace)
                        },
                    }

                    dispatch({
                        type: UserContextAction.BOOTSTRAP_SUCCESS,
                        preferences: preferencesContext,
                    })
                }
            } catch (error) {
                dispatch({
                    type: UserContextAction.BOOTSTRAP_ERROR,
                    error: error.message || 'Failed to load user bootstrap preferences',
                })

                simpleNotification('error', 'Failed to load user preferences')
            }
        },
        [appState.currentUser?.id, appState.currentDomain?.id],
    )

    const initializeApplication = useCallback(async () => {
        await appState.initializer
        setAppStateInitialized(true)
    }, [])

    const setupInitialUserContext = useCallback(
        async (user: UserDto) => {
            sendGAEvent('set_current_user', {}, appState)

            await refreshUserContext(user)

            if (!appState.isAuthenticated) {
                // Only call startSession() on initial login (not on page refresh or
                // context re-initialization). startSession() clears all session timestamps
                // and localStorage sync state — calling it on refresh would wipe the
                // valid session state that the FSM orchestrator needs to resume correctly.
                // broadcastSessionRestored() tells other tabs stuck on the expired
                // interstitial to reload and pick up the new session.
                appService.startSession()
                broadcastSessionRestored()
            }
            appState.isAuthenticated = true
            appService.unsetAppLoading()

            // Load bootstrap preferences for the user
            await loadUserBootstrapPreferences(user.id)

            setInitialUserContextLoaded(true)
        },
        [loadUserBootstrapPreferences],
    )

    /**
     * Gets all user data and loads abilities
     * Can be called at anytime to refresh user context
     */
    const refreshUserContext = useCallback(async (user: UserDto) => {
        try {
            await appState.fetchUserDependencies()

            appState.currentUser = user
            await appState.abilityStore.loadAbilitiesForCurrentUser()

            const currentUserDomainIds =
                appState.currentUserDomains?.map((domain: PlatformUserDomainRecord) => domain.id) ?? []

            const userReviewStatus = appService.getG2ReviewStatus()[user.id]
            setReviewState((prev) => ({
                ...prev,
                status: userReviewStatus?.status ?? 'pending',
            }))

            dispatch({ type: UserContextAction.SET_AUTHENTICATED_USER, user, currentUserDomainIds })

            // TODO: migrate appState user auto refresh here
        } catch (error) {
            setLoadError(error)
        }
    }, [])

    const requestG2ReviewFormAccessToken = async () => {
        if (!userContext.authenticated) return

        const userReviewState = appService.getG2ReviewStatus()[userContext.user.id]
        const nextReviewState: { status: UserReviewStatus; url?: string; loading: boolean } = {
            status: userReviewState?.status ?? UserReviewStatus.PENDING,
            loading: false,
        }

        // reset to pending when lua expires
        if (
            userReviewState?.status === UserReviewStatus.DISMISSED &&
            new Date() >= new Date(userReviewState.lua + 7 * 24 * 60 * 60 * 1000)
        ) {
            nextReviewState.status = UserReviewStatus.PENDING
        }

        // if (userReviewState?.status !== UserReviewStatus.COMPLETED) {
        //     try {
        //         const { data } = await userSvc.getG2ReviewEmbedLink(userContext.user.id)
        //
        //         if (data.status !== UserReviewStatus.COMPLETED) {
        //             nextReviewState.url = data.url
        //         } else {
        //             nextReviewState.status = UserReviewStatus.COMPLETED
        //         }
        //     } catch (error) {
        //         console.debug('Error requesting G2 review form access token: ', { error })
        //     }
        // }

        setReviewState(nextReviewState)
    }

    React.useEffect(() => {
        if (userContext.authenticated) {
            requestG2ReviewFormAccessToken()
        }
    }, [userContext.authenticated])

    React.useEffect(() => {
        const loadUser = async () => {
            if (!inAuth0Flow.current && loc.pathname === AUTH0_PLATFORM_REDIRECT_PATHNAME) {
                inAuth0Flow.current = true

                const params = parseQueryStringParams()
                const code = params.get('code')?.trim()
                const state = params.get('state')?.trim()
                const stateSessionValue = window.sessionStorage.getItem(AUTH0_STATE_SESSION_KEY)

                if (code && state === stateSessionValue) {
                    try {
                        const stateVal: PushlyAuth0State = JSON.parse(atob(state))

                        // Clear session expired flag when starting token exchange
                        // This allows a fresh login after session expiration
                        if (appState.sessionExpired) {
                            appState.sessionExpired = false
                        }

                        // Clear logout flag when starting new auth flow
                        if (appState.isLoggingOut) {
                            appState.isLoggingOut = false
                        }

                        await authorizeAndLoadUserViaAuth0Code(code)

                        if (stateVal.redirectURL) {
                            console.debug('Redirecting user to: ', stateVal.redirectURL)
                            history.replace(stateVal.redirectURL)
                        }
                    } catch (err) {
                        setLoadError(err)
                    }
                } else {
                    setLoadError(new Auth0InvalidAuthorizationParamsException())
                }
            }
        }

        loadUser()
    }, [inAuth0Flow.current, loc.pathname])

    useEffect(() => {
        // prevent unnecessary revalidate user, until code has been traded for auth access token - causing early
        // unset loading states / behaviors
        const isInAuthFlow = loc.pathname === AUTH0_PLATFORM_REDIRECT_PATHNAME || loc.pathname.includes('/authorize')

        if (!inAuth0Flow.current && !isInAuthFlow) {
            // Only check session expiration if we're authenticated
            // If not authenticated, we should attempt to validate session (which will fail and redirect to login)
            if (appState.isAuthenticated && appState.sessionExpired) {
                // Session is expired and we're authenticated, don't attempt to validate
                setInitialUserContextLoaded(true)
                appService.unsetAppLoading()
            } else {
                // Either not authenticated or session not expired - attempt validation
                loadUserViaSession()
            }
        }
    }, [inAuth0Flow.current, loc.pathname, appState.isAuthenticated, appState.sessionExpired])

    useEffect(() => {
        initializeApplication()
    }, [])

    if (loadError instanceof Auth0InvalidAuthorizationParamsException) {
        const qs = new URLSearchParams(loc.search)

        const auth0Error = qs.get('error')
        const auth0ErrorMessage = qs.get('error_description') ?? undefined

        /**
         * Adjust messaging for server_error responses from Auth0.
         */
        const code = auth0Error === 'server_error' ? 503 : 403
        const label = code === 503 ? auth0ErrorMessage : undefined
        const subLabel =
            code === 503 ? 'If the issue persists contact your account manager for more information.' : undefined

        return (
            <ErrorPage
                errorCode={code}
                showAction={true}
                hideLogo={code !== 503}
                label={label}
                subLabel={
                    subLabel ? (
                        <div style={{ fontSize: '1.25em' }}>
                            {subLabel}
                            <br />
                            <br />
                        </div>
                    ) : undefined
                }
            />
        )
    } else if (loadError && !(loadError instanceof SessionValidationFailedException)) {
        return <ErrorPage errorCode={403} showAction={true} hideLogo={true} />
    } else if (!appStateInitialized || !initialUserContextLoaded) {
        return <ModuleLoadingScreen loading={true} variant="app" />
    }

    return (
        <BasePlatformUserContext.Provider value={userContext}>
            <PlatformUserContext.Provider value={{ ...combinedContextValue, assignUser }}>
                <PlatformUserDispatchContext.Provider value={dispatch}>{children}</PlatformUserDispatchContext.Provider>
            </PlatformUserContext.Provider>
        </BasePlatformUserContext.Provider>
    )
}
